import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Fade,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import LaptopIcon from '@mui/icons-material/Laptop';
import TimerIcon from '@mui/icons-material/Timer';
import ShieldIcon from '@mui/icons-material/Shield';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import { useSetBrowserTrustMutation, healthWalletApi } from './healthWalletSlice';
import { useAppDispatch } from '../../app/hooks';

interface OnboardingTrustBrowserProps {
  onNext: () => void;
  onBack?: () => void;
}

export const OnboardingTrustBrowser: FC<OnboardingTrustBrowserProps> = ({ onNext, onBack }) => {
  // EXPLICIT choice, none pre-selected (Leo 2026-07-12: radios replaced the Switch — trusting a browser
  // is a decision the user must actively make, not a default they can sail past).
  const [trustChoice, setTrustChoice] = useState<'trusted' | 'untrusted' | null>(null);
  const [setBrowserTrust, { isLoading, isError, error }] = useSetBrowserTrustMutation();
  const dispatch = useAppDispatch();

  // Reset browserTrustConfirmed when trust screen opens
  // This ensures user must confirm again if they navigate back and return
  useEffect(() => {
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        draft.browserTrustConfirmed = false;
      })
    );
  }, [dispatch]);

  const handleContinue = async () => {
    if (trustChoice === null) return; // Continue is disabled until a choice is made
    const trustEnabled = trustChoice === 'trusted';
    try {
      await setBrowserTrust({
        trust: trustEnabled,
      }).unwrap();

      // Optimistic update: set browserTrustConfirmed to true to trigger navigation
      // Backend will persist browserTrusted preference, but navigation is driven by browserTrustConfirmed
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          draft.browserTrusted = trustEnabled;
          draft.browserTrustConfirmed = true; // Triggers useEffect navigation in OnboardingFlow
        })
      );

      onNext();
    } catch (err) {
      console.error('Trust browser setting failed:', err);
    }
  };

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          pt: { xs: '88px', sm: '112px' },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }} aria-hidden="true">
              <DevicesIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8e8e93', letterSpacing: 1.5 }}>
                Step 3 of 4
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Browser Trust
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="sm" sx={{ mt: -2, pb: 6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Fade in>
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#efeaff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                    Trust This Browser?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 400, mx: 'auto' }}>
                    Enable auto-authentication so you don't need to re-verify your identity every time you visit your Health Wallet on this device.
                  </Typography>
                </Box>

                {/* Trust choice — SELECTION CARDS on radio semantics: a RadioGroup whose inputs are
                    visually hidden and whose LABELS are the cards, so keyboard navigation + screen
                    readers work exactly like radios while the skin is modern. NONE selected by default;
                    the chosen card gets its ring + tint (green = trusted, lavender = untrusted) + a
                    check; focus-within outlines the card for keyboard users. */}
                <RadioGroup
                  value={trustChoice ?? ''}
                  onChange={(e) => setTrustChoice(e.target.value as 'trusted' | 'untrusted')}
                  sx={{ gap: 2, mb: 3 }}
                >
                  {([
                    {
                      value: 'trusted' as const,
                      title: 'This browser is trusted',
                      desc: 'You will be automatically authenticated on future visits.',
                      // Shield + STAR ("trusted badge") — deliberately NOT the shield+check
                      // (VerifiedUser): a check on the unselected card implied a selection.
                      Icon: LocalPoliceIcon,
                      ring: '#16a34a', tint: '#f0fdf4', iconBg: '#dcfce7', titleSel: '#166534',
                    },
                    {
                      value: 'untrusted' as const,
                      title: 'Do not trust this browser',
                      desc: 'You will need to verify your identity each time.',
                      Icon: ShieldIcon,
                      ring: BRAND_DARKER, tint: BG, iconBg: '#efeaff', titleSel: '#1c1c1e',
                    },
                  ]).map(({ value, title, desc, Icon, ring, tint, iconBg, titleSel }) => {
                    const selected = trustChoice === value;
                    return (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio sx={{ position: 'absolute', opacity: 0, width: 0, height: 0, p: 0 }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: selected ? iconBg : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background-color 0.2s ease' }}>
                              {/* Icon keeps its option color in BOTH states (Leo — gray read too plain);
                                  the tile/ring/check still light up only on selection. */}
                              <Icon sx={{ color: ring, fontSize: 24 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" fontWeight={700} sx={{ color: selected ? titleSel : '#1c1c1e' }}>
                                {title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {desc}
                              </Typography>
                            </Box>
                            {selected ? <CheckCircleIcon sx={{ color: ring, flexShrink: 0, mt: 0.25 }} /> : null}
                          </Box>
                        }
                        sx={{
                          m: 0,
                          p: 2.5,
                          width: '100%',
                          borderRadius: 3,
                          border: '2px solid',
                          borderColor: selected ? ring : '#e5e7eb',
                          bgcolor: selected ? tint : 'white',
                          boxShadow: selected ? `0 0 0 2px ${value === 'trusted' ? 'rgba(22,163,74,0.15)' : 'rgba(108,76,224,0.15)'}` : 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: selected ? ring : '#d7d3f0' },
                          '&:focus-within': { outline: `2px solid ${BRAND_DARKER}`, outlineOffset: 2 },
                          '& .MuiFormControlLabel-label': { width: '100%' },
                        }}
                      />
                    );
                  })}
                </RadioGroup>

                {/* Info cards */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LaptopIcon sx={{ color: BRAND_DARKER, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Only trust personal devices</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Don't enable this on shared or public computers.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <TimerIcon sx={{ color: BRAND_DARKER, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Trust expires after 90 days</Typography>
                      <Typography variant="caption" color="text.secondary">
                        You'll be prompted to re-verify periodically for security.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <ShieldIcon sx={{ color: BRAND_DARKER, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Revoke anytime</Typography>
                      <Typography variant="caption" color="text.secondary">
                        You can remove trusted browsers from your Health Wallet settings.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Error display */}
                {isError && (
                  <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                    Failed to save browser trust setting. {(error as any)?.data?.message || 'Please try again.'}
                  </Alert>
                )}
              </Box>
            </Fade>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: { xs: 3, md: 4 }, pt: 0, gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                color: BRAND_DARKER,
                borderColor: '#d7d3f0',
                borderRadius: 50,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: BRAND_DARKER, bgcolor: '#f7f7fa' },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
              onClick={handleContinue}
              disabled={isLoading || trustChoice === null}
              sx={{
                bgcolor: BRAND_DARKER,
                borderRadius: 50,
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                '&:hover': { bgcolor: '#5a3fc0' },
              }}
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
